import './Likes.sass';

import React, { FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { LikesProps } from '$core/Likes/Likes.interface';
import { Button, Header, Placeholder, ScrollViewVirtualized } from '$uikit';
import { likesActions, likesSelector } from '$store/likes';
import { Loader, UserCell } from '$shared/components';
import { isBackNavigation } from '$navigation/router';
import { useTranslator } from '$hooks';

const ROW_HEIGHT = 68;

export const Likes: FC<LikesProps> = ({ objectId, objectType }) => {
  const dispatch = useDispatch();
  const isBack = isBackNavigation();
  const { isLoading, isFailed, likes, nextFrom, isLoadingMore } =
    useSelector(likesSelector);
  const t = useTranslator();

  useEffect(() => {
    if (!isBack) {
      dispatch(
        likesActions.loadLikes({
          objectId,
          objectType,
        }),
      );
    }
  }, []);

  const handleRetry = useCallback(() => {
    dispatch(
      likesActions.loadLikes({
        objectId,
        objectType,
      }),
    );
  }, [dispatch, objectId, objectType]);

  const handleLoadMore = useCallback(() => {
    dispatch(
      likesActions.loadMoreLikes({
        objectId,
        objectType,
        nextFrom: nextFrom!,
      }),
    );
  }, [dispatch, nextFrom]);

  function renderRow(index: number) {
    if (index === likes.length - 1 && isLoadingMore) {
      return <Loader />;
    }

    return <UserCell objectId={likes[index]} />;
  }

  function renderContent() {
    if (isLoading && !likes.length) {
      return <Loader isWrapped />;
    }

    if (isFailed) {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={<Button onClick={handleRetry}>{t('failed_button')}</Button>}
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    return (
      <ScrollViewVirtualized
        className="Likes"
        rowCount={likes.length}
        rowHeight={ROW_HEIGHT}
        renderRow={renderRow}
        canLoadMore={!isLoading && !!nextFrom}
        onLoadMore={handleLoadMore}
      />
    );
  }

  return (
    <>
      <Header showCloseButton>{t('likes_title')}</Header>
      {renderContent()}
    </>
  );
};
